// rackRow.ts — runtime composite for the AI/GPU "rack row" preset.
//
// `rack-row` is a SceneKind with **no Blender master**. It expands at render
// time into N cloned `Rack_AI_Master` instances + matching end caps + an
// overhead `CableTray_Master` strip. Optionally it mirrors the row across
// an aisle to form a corridor (the canonical Meta-style composition).
//
// This file is the precedent-mirror of `brandObjects.ts` (which does the same
// thing for `text-label`). The contract:
//
//   - `RACK_ROW_KIND`, `isRackRowKind(kind)`, `defaultRackRowPatch()` —
//     constant + helpers used by SceneBuilder/IsometricBuilder.
//   - `RACK_ROW_CATALOG_ENTRY` + `addVirtualRackRowEntry(groups)` —
//     injects the row into the sidebar catalog under "AI" without it ever
//     touching the Blender bake (catalog is Blender-derived for masters,
//     this is a virtual entry).
//   - `rackRowTag(o)` — stable string for "did the composite shape change?"
//     so SceneBuilder only rebuilds when it really has to.
//   - `createRackRowObject(o)` — async, builds the THREE.Group composite by
//     cloning real master GLBs through `assetRegistry.getInstance()`. NEVER
//     synthesises geometry (Hard rule #1 in web/CLAUDE.md).
//   - `disposeRackRowObject(obj)` — releases the cloned scene graph.
//
// Hard rules followed:
//   1. No procedural geometry — all visible meshes come from masters.
//   2. No direct GLTF loading — everything goes through assetRegistry.
//   4. SceneKind stays the only public type; "Rack_AI_Master" etc. don't
//      leak past this file.

import * as THREE from "three";
import { getInstance, type CatalogEntry } from "./assetRegistry";
import type { SceneKind } from "../data/sceneKinds";
import type { RackRowPreset, SceneObject } from "./types";

export const RACK_ROW_KIND: SceneKind = "rack-row";

export const RACK_ROW_CATALOG_ENTRY: CatalogEntry = {
  kind: RACK_ROW_KIND,
  master: "__runtime_rack_row__",
  label: "Rack row",
  group: "AI",
  available: true,
};

// Authored cabinet footprint, must match Rack_AI_Master / Rack_EndCap_Master
// dimensions in tools/build_rack_masters.py. If those change, change here.
const CABINET_WIDTH = 0.60;       // along X
const CABINET_DEPTH = 1.00;       // along Z (tray runs perpendicular)
const TRAY_LENGTH   = 2.40;       // CableTray_Master length along X
const DEFAULT_AISLE = 2.40;       // metres between mirrored rows (centre-to-centre)
const MIN_COUNT = 1;
const MAX_COUNT = 64;

// Resolved row config: number of cabinets, whether to draw end caps, and
// how many tray segments to lay overhead.
type ResolvedRow = {
  count: number;
  endCaps: boolean;
  trays: number;
};

const PRESET_TABLE: Record<Exclude<RackRowPreset, "custom">, ResolvedRow> = {
  single: { count: 1,  endCaps: false, trays: 1 },
  short:  { count: 4,  endCaps: true,  trays: 1 },
  medium: { count: 8,  endCaps: true,  trays: 2 },
  long:   { count: 16, endCaps: true,  trays: 4 },
  hall:   { count: 32, endCaps: true,  trays: 8 },
};

export function isRackRowKind(kind: SceneKind): boolean {
  return kind === RACK_ROW_KIND;
}

export function defaultRackRowPatch(): Partial<SceneObject> {
  return {
    rowPreset: "medium",
    rowCount: 8,
    rowPair: false,
    rowAisleWidth: DEFAULT_AISLE,
  };
}

export function addVirtualRackRowEntry(
  groups: Map<string, CatalogEntry[]>,
): Map<string, CatalogEntry[]> {
  const aiEntries = groups.get("AI") ?? [];
  if (!aiEntries.some((e) => e.kind === RACK_ROW_KIND)) {
    groups.set("AI", [...aiEntries, RACK_ROW_CATALOG_ENTRY]);
  }
  return groups;
}

function clampCount(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n)) return 1;
  return Math.max(MIN_COUNT, Math.min(MAX_COUNT, Math.round(n)));
}

/** Resolve the row config from the SceneObject's preset + custom count. */
export function resolveRackRow(o: {
  rowPreset?: RackRowPreset;
  rowCount?: number;
}): ResolvedRow {
  const preset = o.rowPreset ?? "medium";
  if (preset === "custom") {
    const count = clampCount(o.rowCount);
    // Custom rows always get end caps; tray count covers the row length.
    const trays = Math.max(1, Math.ceil((count * CABINET_WIDTH) / TRAY_LENGTH));
    return { count, endCaps: true, trays };
  }
  return PRESET_TABLE[preset];
}

/** Stable tag for "did the composite shape change?". */
export function rackRowTag(o: {
  rowPreset?: RackRowPreset;
  rowCount?: number;
  rowPair?: boolean;
  rowAisleWidth?: number;
}): string {
  const r = resolveRackRow(o);
  const pair = o.rowPair ? "pair" : "single";
  const aisle = (o.rowAisleWidth ?? DEFAULT_AISLE).toFixed(2);
  return [r.count, r.endCaps ? "ec" : "nec", r.trays, pair, aisle].join(":");
}

/**
 * Build the composite Group. Cabinets are stamped along +X centred on the
 * group origin; trays sit above them along the same axis. The aisle (when
 * `rowPair`) duplicates the strip along Z.
 *
 * Tray ceiling height: cabinet height (~2.2m) + small clearance.
 */
const TRAY_CEILING_OFFSET = 0.30; // metres above cabinet top

export async function createRackRowObject(o: {
  rowPreset?: RackRowPreset;
  rowCount?: number;
  rowPair?: boolean;
  rowAisleWidth?: number;
}): Promise<THREE.Group> {
  const group = new THREE.Group();
  group.name = "rack-row";
  group.userData.generatedRow = true;

  const cfg = resolveRackRow(o);
  const aisle = Math.max(0.6, o.rowAisleWidth ?? DEFAULT_AISLE);

  // Build a single-row strip; we'll clone for the mirrored side if `rowPair`.
  // Cabinets occupy the row symmetrically around X = 0.
  const rowLength = cfg.count * CABINET_WIDTH;
  const startX = -rowLength / 2 + CABINET_WIDTH / 2;

  // Resolve all GLB instances in parallel — getInstance caches the master,
  // so this is cheap after the first call.
  const cabinetPromises: Promise<THREE.Group>[] = [];
  for (let i = 0; i < cfg.count; i++) cabinetPromises.push(getInstance("rack-ai"));
  const endCapPromises: Promise<THREE.Group>[] = cfg.endCaps
    ? [getInstance("rack-endcap"), getInstance("rack-endcap")]
    : [];
  const trayPromises: Promise<THREE.Group>[] = [];
  for (let i = 0; i < cfg.trays; i++) trayPromises.push(getInstance("cable-tray"));

  const [cabinets, endCaps, trays] = await Promise.all([
    Promise.all(cabinetPromises),
    Promise.all(endCapPromises),
    Promise.all(trayPromises),
  ]);

  // The "side strip" closure stamps a row of cabinets + caps + tray. We call
  // it once for the primary row, and again (with cloned children) for the
  // pair row when `rowPair` is true.
  const buildStrip = (zOffset: number, useExisting: boolean): THREE.Group => {
    const strip = new THREE.Group();
    strip.userData.rowStrip = true;

    // Cabinets
    for (let i = 0; i < cfg.count; i++) {
      const cab = useExisting ? cabinets[i] : (cabinets[i].clone() as THREE.Group);
      cab.position.set(startX + i * CABINET_WIDTH, 0, zOffset);
      strip.add(cab);
    }

    // End caps — one at each end, sitting one cabinet-width past the last cab.
    if (cfg.endCaps && endCaps.length === 2) {
      const leftCap = useExisting ? endCaps[0] : (endCaps[0].clone() as THREE.Group);
      const rightCap = useExisting ? endCaps[1] : (endCaps[1].clone() as THREE.Group);
      leftCap.position.set(startX - CABINET_WIDTH, 0, zOffset);
      rightCap.position.set(startX + cfg.count * CABINET_WIDTH, 0, zOffset);
      strip.add(leftCap);
      strip.add(rightCap);
    }

    // Trays — laid end-to-end overhead. Tray length is fixed; centre them
    // over the cabinet span so excess hangs over the end caps slightly.
    const trayY = 2.20 + TRAY_CEILING_OFFSET; // cabinet height + clearance
    const traySpan = cfg.trays * TRAY_LENGTH;
    const trayStartX = -traySpan / 2 + TRAY_LENGTH / 2;
    for (let i = 0; i < cfg.trays; i++) {
      const tray = useExisting ? trays[i] : (trays[i].clone() as THREE.Group);
      // CableTray_Master is authored with its long axis along Y in Blender
      // local space (which is Three.js Z). We want it lying along X so it
      // runs over the row. Rotate 90° around Y.
      tray.rotation.set(0, Math.PI / 2, 0);
      tray.position.set(trayStartX + i * TRAY_LENGTH, trayY, zOffset);
      strip.add(tray);
    }
    return strip;
  };

  // Primary row uses the actual cloned instances; if pairing, the mirrored
  // row clones them again (the underlying geometry is shared regardless).
  group.add(buildStrip(0, true));
  if (o.rowPair) {
    group.add(buildStrip(aisle, false));
  }

  return group;
}

export function disposeRackRowObject(obj: THREE.Object3D): void {
  obj.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    // Geometry & materials come from the assetRegistry's master cache and are
    // SHARED across instances. Disposing them here would break every other
    // rack-* placement on the canvas. Leave them; only drop the references.
    mesh.geometry = undefined as any;
    mesh.material = undefined as any;
  });
}
